import {
  DEFAULT_MIME_TYPE,
  FILE_TYPE_MIME_MAP,
} from '@constants/fileTypeConstants';

// The single MIME lookup for the app - every download surface goes through this rather
// than keeping its own copy of the table.
export const getMimeTypeForExtension = (extension: string): string =>
  FILE_TYPE_MIME_MAP[extension.toLowerCase().trim().replace(/^\./, '')] ||
  DEFAULT_MIME_TYPE;

// Reverse lookup, for naming a file the API named for us. jpg/jpeg both map to image/jpeg,
// so the first key wins - either extension opens the file.
export const getExtensionForMimeType = (mimeType: string): string | null =>
  Object.keys(FILE_TYPE_MIME_MAP).find(
    ext => FILE_TYPE_MIME_MAP[ext] === mimeType.toLowerCase().trim()
  ) ?? null;

// Hands a Blob straight to the browser. Used by every download surface so a large file is
// never re-encoded or copied on the way out (BUG 21562).
export const saveBlob = (blob: Blob, fileName: string, mimeType?: string) => {
  // The server's Content-Type wins - axios already put it on the Blob, and it knows the
  // file's real type where mimeType is only a guess from the name. Only an untyped blob
  // (or one we built ourselves) needs the guess.
  //
  // slice, not new Blob([blob]) - retyping the response must not copy the file's bytes, which
  // for a large attachment is another full copy in memory.
  const typedBlob =
    blob.type || !mimeType ? blob : blob.slice(0, blob.size, mimeType);
  const url = window.URL.createObjectURL(typedBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick, not inline - some browsers cancel the download otherwise. Without
  // this the object URL keeps the whole file alive for the life of the page.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

export const exportService = <T>(data: T, title: string, fileType: string) => {
  const fileContents = data;
  if (!fileContents) {
    throw new Error('fileContents is not present in the response');
  }

  try {
    const blob = new Blob(
      [Uint8Array.from(atob(fileContents as string), c => c.charCodeAt(0))],
      {
        type: getMimeTypeForExtension(fileType),
      }
    );

    saveBlob(blob, `${title}_${new Date().toLocaleDateString()}.${fileType}`);
  } catch (error) {
    console.error('Error in exportService:', error);
    throw error;
  }
};
